import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const DEFAULT_XML = `<?xml version="1.0" encoding="UTF-8"?>
<catalog>
  <book id="bk101" lang="en">
    <author>Gambardella, Matthew</author>
    <title>XML Developer's Guide</title>
    <price currency="USD">44.95</price>
    <description>An in-depth look at creating applications with XML.</description>
  </book>
  <book id="bk102" lang="en">
    <author>Ralls, Kim</author>
    <title>Midnight Rain</title>
    <price currency="USD">5.95</price>
    <description><![CDATA[A former architect battles corporate zombies & an evil sorceress.]]></description>
  </book>
</catalog>`;

const INDENT_OPTIONS = [
  { key: "2", char: "  ", labelKey: "xml_formatter_indent_2" },
  { key: "4", char: "    ", labelKey: "xml_formatter_indent_4" },
  { key: "tab", char: "\t", labelKey: "xml_formatter_indent_tab" },
];

const TEXT_NODE = 3;
const CDATA_NODE = 4;
const COMMENT_NODE = 8;
const PROCESSING_INSTRUCTION_NODE = 7;
const DOCUMENT_TYPE_NODE = 10;
const ELEMENT_NODE = 1;

const escapeText = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeAttr = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const isWhitespaceOnly = (s) => /^\s*$/.test(s);

const hasOnlyTextChildren = (el) => {
  if (!el.childNodes.length) return false;
  for (const child of el.childNodes) {
    if (child.nodeType === ELEMENT_NODE) return false;
    if (child.nodeType === COMMENT_NODE) return false;
    if (
      child.nodeType !== TEXT_NODE &&
      child.nodeType !== CDATA_NODE
    ) {
      return false;
    }
  }
  return true;
};

const renderAttributes = (el) => {
  if (!el.attributes || el.attributes.length === 0) return "";
  const parts = [];
  for (let i = 0; i < el.attributes.length; i += 1) {
    const a = el.attributes[i];
    parts.push(`${a.name}="${escapeAttr(a.value)}"`);
  }
  return " " + parts.join(" ");
};

const renderInlineChildren = (el) => {
  let out = "";
  for (const child of el.childNodes) {
    if (child.nodeType === TEXT_NODE) {
      out += escapeText(child.nodeValue || "");
    } else if (child.nodeType === CDATA_NODE) {
      out += `<![CDATA[${child.nodeValue || ""}]]>`;
    }
  }
  return out;
};

const formatNode = (node, depth, pad) => {
  const indent = pad.repeat(depth);

  if (node.nodeType === COMMENT_NODE) {
    return `${indent}<!--${node.nodeValue || ""}-->`;
  }
  if (node.nodeType === PROCESSING_INSTRUCTION_NODE) {
    return `${indent}<?${node.target} ${node.data || ""}?>`;
  }
  if (node.nodeType === CDATA_NODE) {
    return `${indent}<![CDATA[${node.nodeValue || ""}]]>`;
  }
  if (node.nodeType === TEXT_NODE) {
    const text = (node.nodeValue || "").trim();
    if (!text) return "";
    return `${indent}${escapeText(text)}`;
  }
  if (node.nodeType !== ELEMENT_NODE) return "";

  const tag = node.nodeName;
  const attrs = renderAttributes(node);

  if (!node.childNodes.length) {
    return `${indent}<${tag}${attrs}/>`;
  }

  if (hasOnlyTextChildren(node)) {
    const inner = renderInlineChildren(node);
    if (!inner.trim()) {
      return `${indent}<${tag}${attrs}/>`;
    }
    return `${indent}<${tag}${attrs}>${inner}</${tag}>`;
  }

  const lines = [`${indent}<${tag}${attrs}>`];
  for (const child of node.childNodes) {
    if (
      child.nodeType === TEXT_NODE &&
      isWhitespaceOnly(child.nodeValue || "")
    ) {
      continue;
    }
    const rendered = formatNode(child, depth + 1, pad);
    if (rendered) lines.push(rendered);
  }
  lines.push(`${indent}</${tag}>`);
  return lines.join("\n");
};

const detectXmlDeclaration = (source) => {
  const match = source.match(/^\s*<\?xml\b[^?]*\?>/i);
  return match ? match[0].trim() : null;
};

const parseXml = (source) => {
  if (typeof DOMParser === "undefined") {
    return { ok: false, errorKey: "xml_formatter_error_unsupported" };
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(source, "text/xml");
  const errorNode = doc.querySelector("parsererror");
  if (errorNode) {
    const detail = errorNode.textContent
      ? errorNode.textContent.trim().split("\n")[0].trim()
      : "";
    return {
      ok: false,
      errorKey: "xml_formatter_error_invalid",
      errorDetail: detail,
    };
  }
  if (!doc.documentElement) {
    return { ok: false, errorKey: "xml_formatter_error_empty" };
  }
  return { ok: true, doc };
};

const formatXml = (source, pad) => {
  const trimmed = source.trim();
  if (!trimmed) {
    return { ok: true, output: "" };
  }
  const parsed = parseXml(trimmed);
  if (!parsed.ok) return parsed;

  const decl = detectXmlDeclaration(trimmed);
  const lines = [];
  if (decl) lines.push(decl);

  // Walk top-level nodes — preserve order so PIs and comments outside the root stay
  for (const node of parsed.doc.childNodes) {
    if (
      node.nodeType === TEXT_NODE &&
      isWhitespaceOnly(node.nodeValue || "")
    ) {
      continue;
    }
    if (node.nodeType === DOCUMENT_TYPE_NODE) {
      const dt = node;
      let s = `<!DOCTYPE ${dt.name}`;
      if (dt.publicId) s += ` PUBLIC "${dt.publicId}"`;
      if (dt.systemId) s += dt.publicId ? ` "${dt.systemId}"` : ` SYSTEM "${dt.systemId}"`;
      s += ">";
      lines.push(s);
      continue;
    }
    const rendered = formatNode(node, 0, pad);
    if (rendered) lines.push(rendered);
  }
  return { ok: true, output: lines.join("\n") };
};

const minifyNode = (node) => {
  if (node.nodeType === COMMENT_NODE) {
    return `<!--${node.nodeValue || ""}-->`;
  }
  if (node.nodeType === PROCESSING_INSTRUCTION_NODE) {
    return `<?${node.target} ${node.data || ""}?>`;
  }
  if (node.nodeType === CDATA_NODE) {
    return `<![CDATA[${node.nodeValue || ""}]]>`;
  }
  if (node.nodeType === TEXT_NODE) {
    const text = node.nodeValue || "";
    if (isWhitespaceOnly(text)) return "";
    return escapeText(text.replace(/\s+/g, " "));
  }
  if (node.nodeType !== ELEMENT_NODE) return "";

  const tag = node.nodeName;
  const attrs = renderAttributes(node);
  if (!node.childNodes.length) {
    return `<${tag}${attrs}/>`;
  }
  let inner = "";
  for (const child of node.childNodes) {
    inner += minifyNode(child);
  }
  if (!inner) {
    return `<${tag}${attrs}/>`;
  }
  return `<${tag}${attrs}>${inner}</${tag}>`;
};

const minifyXml = (source, stripComments) => {
  const trimmed = source.trim();
  if (!trimmed) return { ok: true, output: "" };
  const parsed = parseXml(trimmed);
  if (!parsed.ok) return parsed;

  const decl = detectXmlDeclaration(trimmed);
  let out = decl || "";

  for (const node of parsed.doc.childNodes) {
    if (
      node.nodeType === TEXT_NODE &&
      isWhitespaceOnly(node.nodeValue || "")
    ) {
      continue;
    }
    if (node.nodeType === COMMENT_NODE && stripComments) continue;
    if (node.nodeType === DOCUMENT_TYPE_NODE) {
      const dt = node;
      let s = `<!DOCTYPE ${dt.name}`;
      if (dt.publicId) s += ` PUBLIC "${dt.publicId}"`;
      if (dt.systemId) s += dt.publicId ? ` "${dt.systemId}"` : ` SYSTEM "${dt.systemId}"`;
      s += ">";
      out += s;
      continue;
    }
    if (node.nodeType === ELEMENT_NODE && stripComments) {
      out += minifyElementStripping(node);
    } else {
      out += minifyNode(node);
    }
  }
  return { ok: true, output: out };
};

const minifyElementStripping = (node) => {
  const tag = node.nodeName;
  const attrs = renderAttributes(node);
  if (!node.childNodes.length) {
    return `<${tag}${attrs}/>`;
  }
  let inner = "";
  for (const child of node.childNodes) {
    if (child.nodeType === COMMENT_NODE) continue;
    if (child.nodeType === ELEMENT_NODE) {
      inner += minifyElementStripping(child);
    } else {
      inner += minifyNode(child);
    }
  }
  if (!inner) return `<${tag}${attrs}/>`;
  return `<${tag}${attrs}>${inner}</${tag}>`;
};

const XmlFormatter = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(DEFAULT_XML);
  const [mode, setMode] = useState("format");
  const [indentKey, setIndentKey] = useState("2");
  const [stripComments, setStripComments] = useState(false);

  const pad = useMemo(
    () => INDENT_OPTIONS.find((o) => o.key === indentKey)?.char ?? "  ",
    [indentKey],
  );

  const result = useMemo(() => {
    if (mode === "minify") return minifyXml(input, stripComments);
    return formatXml(input, pad);
  }, [input, mode, pad, stripComments]);

  const stats = useMemo(() => {
    const inputBytes = new Blob([input]).size;
    const outputBytes = result.ok && result.output ? new Blob([result.output]).size : 0;
    return { inputBytes, outputBytes };
  }, [input, result]);

  const copy = async () => {
    if (!result.ok || !result.output) {
      toast.error(t("xml_formatter_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(result.output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const download = () => {
    if (!result.ok || !result.output) {
      toast.error(t("xml_formatter_nothing_to_copy"));
      return;
    }
    const filename = mode === "minify" ? "minified.xml" : "formatted.xml";
    const blob = new Blob([result.output], { type: "application/xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clear = () => setInput("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("xml_formatter_title")}
            </h1>
            <p className="text-gray-600">{t("xml_formatter_intro")}</p>

            <div className="flex flex-wrap items-center gap-2">
              <StandardButton
                onClick={() => setMode("format")}
                type={mode === "format" ? "primary" : "secondary"}
                variant={mode === "format" ? undefined : "outlined"}
                size="small"
              >
                {t("xml_formatter_mode_format")}
              </StandardButton>
              <StandardButton
                onClick={() => setMode("minify")}
                type={mode === "minify" ? "primary" : "secondary"}
                variant={mode === "minify" ? undefined : "outlined"}
                size="small"
              >
                {t("xml_formatter_mode_minify")}
              </StandardButton>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              {mode === "format" && (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-gray-700">
                    {t("xml_formatter_indent_label")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {INDENT_OPTIONS.map((o) => (
                      <StandardButton
                        key={o.key}
                        onClick={() => setIndentKey(o.key)}
                        type={indentKey === o.key ? "primary" : "secondary"}
                        variant={indentKey === o.key ? undefined : "outlined"}
                        size="small"
                      >
                        {t(o.labelKey)}
                      </StandardButton>
                    ))}
                  </div>
                </div>
              )}

              {mode === "minify" && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={stripComments}
                    onChange={(e) => setStripComments(e.target.checked)}
                    className="h-4 w-4"
                  />
                  {t("xml_formatter_strip_comments")}
                </label>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("xml_formatter_input_label")}
                </label>
                <StandardButton
                  onClick={clear}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={!input}
                >
                  {t("xml_formatter_clear")}
                </StandardButton>
              </div>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("xml_formatter_input_placeholder")}
                rows={10}
                spellCheck={false}
                aria-label={t("xml_formatter_input_label")}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("xml_formatter_output_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={download}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!result.ok || !result.output}
                  >
                    {t("xml_formatter_download")}
                  </StandardButton>
                  <StandardButton
                    onClick={copy}
                    type="primary"
                    size="small"
                    disabled={!result.ok || !result.output}
                  >
                    {t("copy_button")}
                  </StandardButton>
                </div>
              </div>
              {result.ok ? (
                <pre className="w-full min-h-[6rem] max-h-[24rem] overflow-auto p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre text-gray-800">
                  {result.output || (
                    <em className="text-gray-400 not-italic">
                      {t("xml_formatter_output_empty")}
                    </em>
                  )}
                </pre>
              ) : (
                <p className="text-sm text-red-600 break-all" role="alert">
                  {t(result.errorKey)}
                  {result.errorDetail ? ` — ${result.errorDetail}` : ""}
                </p>
              )}
              {result.ok && result.output && (
                <div className="text-xs text-gray-500">
                  {t("xml_formatter_stats", {
                    inputBytes: stats.inputBytes,
                    outputBytes: stats.outputBytes,
                  })}
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("xml_formatter_note_parser")}</p>
              <p>{t("xml_formatter_note_entities")}</p>
              <p>{t("xml_formatter_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default XmlFormatter;
